import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Textarea,
  Switch,
  FormHelperText,
  Grid,
  Select,
  IconButton,
} from '@chakra-ui/react'
import { Plus, Trash2 } from 'lucide-react'
import {
  createClientSchema,
  updateClientSchema,
  sanitizePurchaseOrderId,
  type ClientEmailDomainFormData,
  type ClientContactEmailFormData,
  type CreateClientFormData,
  type UpdateClientFormData,
} from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  ClientProvisioningModal,
  type ProvisioningPhase,
  type ProvisioningStep,
} from '@/components/forms/ClientProvisioningModal'

interface ClientFormProps {
  clientId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type ProvisionStatus = {
  status: string
  database_name: string
  database_exists: boolean
  provisioned_at?: string | null
  error?: string | null
}

const emptyEmailDomain = (): ClientEmailDomainFormData => ({
  domain: '',
  type: 'corporate',
  is_primary: false,
  notes: '',
})

const emptyContactEmail = (): ClientContactEmailFormData => ({
  email: '',
  notes: '',
})

const DOMAIN_TYPE_OPTIONS = [
  { value: 'corporate', label: 'Corporate (direct match)' },
  { value: 'ticketing_platform', label: 'Ticketing platform (needs keywords)' },
  { value: 'msp_shared', label: 'MSP / shared (needs keywords)' },
] as const

const mapEmailDomainsFromApi = (domains: unknown): ClientEmailDomainFormData[] => {
  if (!Array.isArray(domains)) return []
  return domains.map((row) => {
    const d = row as Record<string, unknown>
    return {
      id: sanitizePurchaseOrderId(d.id),
      domain: String(d.domain ?? '').trim(),
      type: (d.type as ClientEmailDomainFormData['type']) ?? 'corporate',
      is_primary: Boolean(d.is_primary),
      notes: d.notes ? String(d.notes) : '',
    }
  })
}

const mapContactEmailsFromApi = (emails: unknown): ClientContactEmailFormData[] => {
  if (!Array.isArray(emails)) return []
  return emails.map((row) => {
    const e = row as Record<string, unknown>
    return {
      id: sanitizePurchaseOrderId(e.id),
      email: String(e.email ?? '').trim(),
      notes: e.notes ? String(e.notes) : '',
    }
  })
}

const defaultProvisioningSteps = (): ProvisioningStep[] => [
  { key: 'validate', label: 'Validating database name', status: 'pending' },
  { key: 'create_database', label: 'Creating database', status: 'pending' },
  { key: 'run_migrations', label: 'Running SIMA migrations', status: 'pending' },
  { key: 'seed_catalog_roots', label: 'Seeding asset catalog roots', status: 'pending' },
  { key: 'finalize', label: 'Finalizing', status: 'pending' },
]

export function ClientForm({ clientId, onSuccess, onCancel }: ClientFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!clientId

  const [provisionStatus, setProvisionStatus] = useState<ProvisionStatus | null>(null)
  const [showCreateDatabase, setShowCreateDatabase] = useState(false)
  const [isProvisioning, setIsProvisioning] = useState(false)

  const [provisioningOpen, setProvisioningOpen] = useState(false)
  const [provisioningPhase, setProvisioningPhase] = useState<ProvisioningPhase>('saving_client')
  const [provisioningSteps, setProvisioningSteps] = useState<ProvisioningStep[]>(defaultProvisioningSteps())
  const [provisioningDatabaseName, setProvisioningDatabaseName] = useState<string | null>(null)
  const [provisioningError, setProvisioningError] = useState<string | null>(null)
  const [pendingClientId, setPendingClientId] = useState<number | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<CreateClientFormData | UpdateClientFormData>({
    resolver: zodResolver(isEditMode ? updateClientSchema : createClientSchema),
    defaultValues: {
      code: '',
      name: '',
      status: true,
      config: null,
      description: '',
      email_domains: [],
      contact_emails: [],
    },
  })

  const statusValue = watch('status')
  const emailDomains = watch('email_domains') ?? []
  const contactEmails = watch('contact_emails') ?? []

  const loadProvisionStatus = useCallback(async (id: number) => {
    try {
      const res = await apiClient.get(`/organization/clients/${id}/provision-status`)
      const data = (res.data?.data ?? res.data) as ProvisionStatus
      setProvisionStatus(data)
      const needsDatabase = !data.database_exists || data.status !== 'completed'
      setShowCreateDatabase(needsDatabase)
    } catch {
      setProvisionStatus(null)
      setShowCreateDatabase(false)
    }
  }, [])

  useEffect(() => {
    if (isEditMode && clientId) {
      const fetchClient = async () => {
        setIsLoading(true)
        try {
          const response = await apiClient.get(`/organization/clients/${clientId}`)
          const client = response.data?.data || response.data || {}

          reset({
            code: client.code || '',
            name: client.name || '',
            status: client.status !== undefined ? Boolean(client.status) : true,
            config: null,
            description: client.description || '',
            email_domains: mapEmailDomainsFromApi(client.email_domains),
            contact_emails: mapContactEmailsFromApi(client.contact_emails),
          })

          await loadProvisionStatus(clientId)
        } catch (error: any) {
          showError(error)
        } finally {
          setIsLoading(false)
        }
      }
      void fetchClient()
    }
  }, [clientId, isEditMode, reset, showError, loadProvisionStatus])

  const buildPayload = (data: CreateClientFormData | UpdateClientFormData) => ({
    ...data,
    email_domains: (data.email_domains ?? [])
      .map((row) => ({
        ...row,
        domain: row.domain?.trim() || '',
        notes: row.notes?.trim() || null,
      }))
      .filter((row) => row.domain !== ''),
    contact_emails: (data.contact_emails ?? [])
      .map((row) => ({
        ...row,
        email: row.email?.trim() || '',
        notes: row.notes?.trim() || null,
      }))
      .filter((row) => row.email !== ''),
    config: null,
  })

  const runProvisioning = async (id: number, databaseName?: string | null) => {
    setIsProvisioning(true)
    setProvisioningOpen(true)
    setProvisioningPhase('provisioning_database')
    setProvisioningSteps(defaultProvisioningSteps())
    setProvisioningDatabaseName(databaseName ?? null)
    setProvisioningError(null)
    setPendingClientId(id)

    try {
      const res = await apiClient.post(`/organization/clients/${id}/provision-database`)
      const result = res.data?.data ?? res.data
      const steps = (result?.steps ?? defaultProvisioningSteps()) as ProvisioningStep[]
      setProvisioningSteps(steps)
      setProvisioningDatabaseName(result?.database_name ?? databaseName ?? null)

      if (result?.status === 'completed') {
        setProvisioningPhase('done')
        setShowCreateDatabase(false)
        setProvisionStatus({
          status: 'completed',
          database_name: result.database_name,
          database_exists: true,
        })
        showSuccess('Success', 'Client database provisioned successfully')
        return true
      }

      setProvisioningPhase('failed')
      setProvisioningError(result?.error ?? 'Database provisioning failed')
      return false
    } catch (error: any) {
      const result = error?.response?.data?.data ?? error?.response?.data
      if (result?.steps) {
        setProvisioningSteps(result.steps)
      }
      setProvisioningPhase('failed')
      setProvisioningError(result?.error ?? error?.message ?? 'Database provisioning failed')
      return false
    } finally {
      setIsProvisioning(false)
    }
  }

  const onSubmit = async (data: CreateClientFormData | UpdateClientFormData) => {
    setIsSubmitting(true)
    try {
      const payload = buildPayload(data)

      if (isEditMode && clientId) {
        const { code: _code, ...updatePayload } = payload
        await apiClient.put(`/organization/clients/${clientId}`, updatePayload)
        showSuccess('Success', 'Client updated successfully')
        if (onSuccess) onSuccess()
        return
      }

      setProvisioningOpen(true)
      setProvisioningPhase('saving_client')
      setProvisioningSteps(defaultProvisioningSteps())
      setProvisioningError(null)

      const createRes = await apiClient.post('/organization/clients', payload)
      const created = createRes.data?.data ?? createRes.data
      const newId = Number(created?.id)
      const dbName = created?.database_name ?? null

      if (!newId || Number.isNaN(newId)) {
        throw new Error('Client was created but no ID was returned')
      }

      setPendingClientId(newId)
      setProvisioningDatabaseName(dbName)

      const provisioned = await runProvisioning(newId, dbName)
      if (provisioned && onSuccess) {
        setProvisioningOpen(false)
        onSuccess()
      }
    } catch (error: any) {
      if (!isEditMode) {
        setProvisioningPhase('failed')
        setProvisioningError(error?.response?.data?.message ?? error?.message ?? 'Failed to create client')
        setProvisioningOpen(true)
      } else {
        showError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCreateDatabase = async () => {
    if (!clientId) return
    const dbName = provisionStatus?.database_name ?? provisioningDatabaseName
    await runProvisioning(clientId, dbName)
  }

  const handleProvisioningRetry = async () => {
    const id = pendingClientId ?? clientId
    if (!id) return
    await runProvisioning(id, provisioningDatabaseName ?? provisionStatus?.database_name)
  }

  const handleProvisioningClose = () => {
    setProvisioningOpen(false)
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading client data...</Text>
      </Box>
    )
  }

  return (
    <Box>
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Client Name <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.name}>
              <Input {...register('name')} placeholder="e.g., Jollibee Foods Corporation" />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Client Code <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.code}>
              <Input
                {...register('code', {
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase()
                  },
                })}
                placeholder="e.g., JFC"
                textTransform="uppercase"
                isDisabled={isEditMode}
              />
              <FormHelperText>
                Uppercase letters, numbers, and underscores only. Cannot be changed after creation.
              </FormHelperText>
              <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            </FormControl>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Email Domains
            </FormLabel>
            <Box>
              <VStack align="stretch" spacing={3}>
                {emailDomains.map((row, index) => (
                  <Box key={row.id ?? `domain-${index}`} p={3} borderWidth="1px" borderRadius="md" borderColor={colors.border}>
                    <Grid templateColumns={{ base: '1fr', md: '2fr 1fr auto auto' }} gap={3} alignItems="end">
                      <FormControl isInvalid={!!errors.email_domains?.[index]?.domain}>
                        <FormLabel fontSize="sm">Domain</FormLabel>
                        <Input
                          {...register(`email_domains.${index}.domain`)}
                          placeholder="e.g. hpservices.ph"
                        />
                        <FormErrorMessage>{errors.email_domains?.[index]?.domain?.message}</FormErrorMessage>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Type</FormLabel>
                        <Select {...register(`email_domains.${index}.type`)}>
                          {DOMAIN_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl display="flex" alignItems="center" pb={2}>
                        <Switch
                          {...register(`email_domains.${index}.is_primary`)}
                          colorScheme="blue"
                        />
                        <FormLabel mb={0} ml={2} fontSize="sm">
                          Primary
                        </FormLabel>
                      </FormControl>
                      <IconButton
                        aria-label="Remove domain"
                        icon={<Trash2 size={16} />}
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => {
                          const next = emailDomains.filter((_, i) => i !== index)
                          setValue('email_domains', next, { shouldValidate: true })
                        }}
                      />
                    </Grid>
                  </Box>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Plus size={16} />}
                  alignSelf="flex-start"
                  onClick={() => setValue('email_domains', [...emailDomains, emptyEmailDomain()])}
                >
                  Add domain
                </Button>
                <Text fontSize="sm" color="gray.500">
                  Incoming support emails matching these domains route to this client. Shared/platform types need subject keywords.
                </Text>
              </VStack>
            </Box>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Contact Emails
            </FormLabel>
            <Box>
              <VStack align="stretch" spacing={3}>
                {contactEmails.map((row, index) => (
                  <HStack key={row.id ?? `email-${index}`} align="end">
                    <FormControl flex={1} isInvalid={!!errors.contact_emails?.[index]?.email}>
                      <FormLabel fontSize="sm">Exact email</FormLabel>
                      <Input
                        {...register(`contact_emails.${index}.email`)}
                        placeholder="e.g. premier@lenovo.com"
                      />
                      <FormErrorMessage>{errors.contact_emails?.[index]?.email?.message}</FormErrorMessage>
                    </FormControl>
                    <IconButton
                      aria-label="Remove contact email"
                      icon={<Trash2 size={16} />}
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        const next = contactEmails.filter((_, i) => i !== index)
                        setValue('contact_emails', next, { shouldValidate: true })
                      }}
                    />
                  </HStack>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Plus size={16} />}
                  alignSelf="flex-start"
                  onClick={() => setValue('contact_emails', [...contactEmails, emptyContactEmail()])}
                >
                  Add contact email
                </Button>
                <Text fontSize="sm" color="gray.500">
                  Specific sender addresses that always map to this client (allowlist).
                </Text>
              </VStack>
            </Box>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Description
            </FormLabel>
            <FormControl isInvalid={!!errors.description}>
              <Textarea
                {...register('description')}
                placeholder="Optional description for this client"
                rows={4}
                maxLength={1000}
              />
              <FormHelperText>Maximum 1000 characters</FormHelperText>
              <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
            </FormControl>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Status
            </FormLabel>
            <FormControl>
              <HStack>
                <Switch
                  isChecked={statusValue}
                  onChange={(e) => setValue('status', e.target.checked)}
                  colorScheme="green"
                />
                <Text fontSize="sm" color={colors.text}>
                  {statusValue ? 'Active' : 'Inactive'}
                </Text>
              </HStack>
              <FormHelperText>Active clients can be accessed by users</FormHelperText>
            </FormControl>
          </Grid>

          {isEditMode && showCreateDatabase ? (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <AlertDescription flex="1">
                This client does not have a provisioned SIMA database yet.
                {provisionStatus?.database_name ? ` (${provisionStatus.database_name})` : ''}
                {provisionStatus?.error ? ` Last error: ${provisionStatus.error}` : ''}
              </AlertDescription>
              <Button
                size="sm"
                colorScheme="blue"
                ml={3}
                onClick={() => void handleCreateDatabase()}
                isLoading={isProvisioning}
                loadingText="Creating..."
              >
                Create Database
              </Button>
            </Alert>
          ) : null}

          <HStack spacing={4} justify="flex-end" pt={4}>
            {onCancel && (
              <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting || isProvisioning}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isSubmitting}
              loadingText={isEditMode ? 'Saving...' : 'Creating...'}
              isDisabled={isProvisioning}
            >
              {isEditMode ? 'Save' : 'Create Client'}
            </Button>
          </HStack>
        </VStack>
      </Box>

      <ClientProvisioningModal
        isOpen={provisioningOpen}
        phase={provisioningPhase}
        steps={provisioningSteps}
        databaseName={provisioningDatabaseName}
        error={provisioningError}
        onRetry={handleProvisioningRetry}
        onClose={handleProvisioningClose}
      />
    </Box>
  )
}
